import type { InternalAxiosRequestConfig } from "axios";

const FRESHNESS_WINDOW_MS = 5 * 60 * 1000;
const WATCH_MAX_AGE_MS = FRESHNESS_WINDOW_MS;
const STATIONARY_REFRESH_INTERVAL_MS = 60 * 1000;

export interface LocationFix {
  lat: number;
  lng: number;
  accuracyMeters: number;
  capturedAt: string;
}

declare global {
  interface Window {
    __SERVENGINE_LOCATION__?: {
      lat: number;
      lng: number;
      accuracyMeters: number;
      capturedAt: string;
    };
  }
}

let latestWatchFix: LocationFix | null = null;
let watchId: number | null = null;
let stationaryRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
// Bumped every time the watch is torn down, so an initLocationWatchIfPermitted()
// call that is still awaiting the Permissions API when logout/unmount happens
// can tell its answer is stale and skip starting a watch nothing will ever stop.
let watchGeneration = 0;

function isFresh(capturedAt: string): boolean {
  const capturedTime = new Date(capturedAt).getTime();
  return !Number.isNaN(capturedTime) && Date.now() - capturedTime < FRESHNESS_WINDOW_MS;
}

function isLocationFix(value: unknown): value is LocationFix {
  if (!value || typeof value !== "object") return false;
  const { lat, lng, accuracyMeters, capturedAt } = value as Record<string, unknown>;
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    typeof accuracyMeters === "number" &&
    typeof capturedAt === "string"
  );
}

function getWebViewFix(): LocationFix | null {
  const injected = window.__SERVENGINE_LOCATION__;
  return isLocationFix(injected) ? injected : null;
}

export function getLatestLocationFix(): LocationFix | null {
  const webViewFix = getWebViewFix();
  if (webViewFix && isFresh(webViewFix.capturedAt)) {
    return webViewFix;
  }

  if (latestWatchFix && isFresh(latestWatchFix.capturedAt)) {
    return latestWatchFix;
  }

  return null;
}

function trimTrailingZeros(numericString: string): string {
  if (!numericString.includes(".")) return numericString;
  return numericString.replace(/0+$/, "").replace(/\.$/, "");
}

function formatCoordinate(value: number): string {
  return trimTrailingZeros(value.toFixed(6));
}

function formatAccuracy(value: number): string {
  return trimTrailingZeros(value.toFixed(1));
}

export function formatLocationHeader(fix: LocationFix): string {
  return `lat=${formatCoordinate(fix.lat)};lng=${formatCoordinate(fix.lng)};acc=${formatAccuracy(fix.accuracyMeters)};ts=${fix.capturedAt}`;
}

export function getLocationHeaderValue(): string | undefined {
  const fix = getLatestLocationFix();
  return fix ? formatLocationHeader(fix) : undefined;
}

function handlePositionUpdate(position: GeolocationPosition): void {
  latestWatchFix = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracyMeters: position.coords.accuracy,
    capturedAt: new Date(position.timestamp).toISOString(),
  };
}

function startStationaryRefresh(): void {
  if (stationaryRefreshIntervalId !== null) return;

  // watchPosition only fires on movement, so a stationary user's fix would go
  // stale after FRESHNESS_WINDOW_MS with no update. Force a fresh read on an
  // interval shorter than that window, bypassing the watch's own cache.
  stationaryRefreshIntervalId = setInterval(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(handlePositionUpdate, () => {}, {
      enableHighAccuracy: true,
      maximumAge: 0,
    });
  }, STATIONARY_REFRESH_INTERVAL_MS);
}

function startWatch(): void {
  if (watchId !== null || typeof navigator === "undefined" || !navigator.geolocation) {
    return;
  }

  watchId = navigator.geolocation.watchPosition(
    handlePositionUpdate,
    () => {
      // A watch error (permission revoked, position unavailable) just means we stop
      // producing fresh fixes; the header is omitted once the last fix goes stale.
    },
    { enableHighAccuracy: true, maximumAge: WATCH_MAX_AGE_MS },
  );
  startStationaryRefresh();
}

export function stopLocationWatch(): void {
  watchGeneration += 1;

  if (watchId !== null && typeof navigator !== "undefined" && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }
  watchId = null;
  latestWatchFix = null;

  if (stationaryRefreshIntervalId !== null) {
    clearInterval(stationaryRefreshIntervalId);
    stationaryRefreshIntervalId = null;
  }
}

export async function initLocationWatchIfPermitted(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.permissions || !navigator.geolocation) {
    return;
  }

  const generationAtCallTime = watchGeneration;

  try {
    const status = await navigator.permissions.query({
      name: "geolocation" as PermissionName,
    });
    // stopLocationWatch() ran while we were awaiting the permission check
    // (logout, unmount) — starting the watch now would leak it.
    if (status.state === "granted" && generationAtCallTime === watchGeneration) {
      startWatch();
    }
  } catch {
    // Permissions API doesn't support querying "geolocation" in this browser; the
    // explicit requestLocationPermission() path is still available to the user.
  }
}

export function requestLocationPermission(): Promise<PermissionState> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve("denied");
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handlePositionUpdate(position);
        startWatch();
        resolve("granted");
      },
      () => resolve("denied"),
      { enableHighAccuracy: true },
    );
  });
}

const LOCATION_ASKED_STORAGE_KEY = "servengine-location-asked";

function hasAskedForLocation(): boolean {
  try {
    return localStorage.getItem(LOCATION_ASKED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberLocationAsked(): void {
  try {
    localStorage.setItem(LOCATION_ASKED_STORAGE_KEY, "true");
  } catch {
    // Without storage the filler may be asked again on a later visit; never worse than that.
  }
}

async function queryLocationPermission(): Promise<PermissionState | null> {
  if (!navigator.permissions) return null;
  try {
    const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
    return status.state;
  } catch {
    return null;
  }
}

// Location is optional on form fill: a filler who ignores or dismisses the prompt is
// never asked again, and nothing waits on the answer.
export async function askLocationOnce(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return;

  const generationAtCallTime = watchGeneration;
  const permission = await queryLocationPermission();
  if (generationAtCallTime !== watchGeneration) return;

  if (permission === "granted") {
    startWatch();
    return;
  }
  if (permission === "denied" || hasAskedForLocation()) return;

  rememberLocationAsked();
  void requestLocationPermission();
}

export function attachLocationHeader<T extends InternalAxiosRequestConfig>(config: T): T {
  try {
    const locationHeaderValue = getLocationHeaderValue();
    if (locationHeaderValue) {
      config.headers["X-Location"] = locationHeaderValue;
    }
  } catch {
    // A malformed WebView global or an unsupported geolocation API must never
    // block the request; the header is simply omitted.
  }
  return config;
}

export function __resetLocationStateForTests(): void {
  if (stationaryRefreshIntervalId !== null) {
    clearInterval(stationaryRefreshIntervalId);
  }
  latestWatchFix = null;
  watchId = null;
  stationaryRefreshIntervalId = null;
  watchGeneration = 0;
}
